export type ComponentFunctionStatus =
  'queued' | 'running' | 'succeeded' | 'failed' | 'timed_out' | 'indeterminate';

export interface ComponentFunctionRunView {
  id: string;
  name: string;
  status: ComponentFunctionStatus;
  result?: unknown;
  error?: { code: string; message: string };
  logs: Array<{ level: 'info' | 'warn' | 'error'; message: string }>;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
}

/** 此函数会被序列化到隔离 iframe，不能依赖模块外变量或宿主对象。 */
export function createComponentFunctionApi(
  call: (method: string, payload: Record<string, unknown>) => Promise<unknown>,
) {
  const environment = globalThis as unknown as {
    crypto: { getRandomValues<T extends Uint8Array>(array: T): T };
    setTimeout(callback: () => void, delay: number): unknown;
  };
  const start = (name: string, input: unknown, options: { idempotencyKey?: string } = {}) =>
    call('functions.invoke', {
      name,
      input,
      idempotencyKey:
        options.idempotencyKey ??
        Array.from(environment.crypto.getRandomValues(new Uint8Array(16)))
          .map((value) => value.toString(16).padStart(2, '0'))
          .join(''),
    }) as Promise<ComponentFunctionRunView>;
  const get = (runId: string) =>
    call('functions.get', { runId }) as Promise<ComponentFunctionRunView>;
  return Object.freeze({
    start,
    get,
    list: () => call('functions.list', {}) as Promise<ComponentFunctionRunView[]>,
    invoke: async (name: string, input: unknown, options: { idempotencyKey?: string } = {}) => {
      let run = await start(name, input, options);
      const deadline = Date.now() + 180_000;
      while (run.status === 'queued' || run.status === 'running') {
        if (Date.now() >= deadline)
          throw Object.assign(new Error('等待云函数结果超时，可使用 runId 查询原任务'), {
            code: 'FUNCTION_WAIT_TIMEOUT',
            runId: run.id,
          });
        await new Promise<void>((resolve) => environment.setTimeout(resolve, 500));
        try {
          run = await get(run.id);
        } catch (error) {
          throw Object.assign(error instanceof Error ? error : new Error('查询云函数结果失败'), {
            runId: run.id,
          });
        }
      }
      if (run.status !== 'succeeded')
        throw Object.assign(new Error(run.error?.message ?? '云函数执行失败'), {
          code: run.error?.code ?? 'FUNCTION_FAILED',
          runId: run.id,
        });
      return run.result;
    },
  });
}
